// ─────────────────────────────────────────────────────────────────────────────
// WHEN A WRITE FAILS, SAY SO WHERE THE PERSON IS LOOKING.
//
// Carla, 2026-10-03, after a branding save was refused with a 403 and she never
// saw it: "The error WAS rendered, at the top of a panel, while I was at the
// bottom filling in company details next to a button that said nothing. Nothing
// lied. Nothing was swallowed. I still never saw it."
//
// And her warning about this very helper: "A shared helper that renders at the
// top of a long panel reproduces exactly this defect with better plumbing."
//
// SO THIS HELPER DOES NOT RENDER ANYTHING ITSELF. It turns an error into a
// sentence, and the CALLER puts that sentence next to the control that failed.
// There is deliberately no <WriteFailureBanner> here to drop at the top of a
// page: the geometry is the whole point, and a component would make the wrong
// geometry the easy path.
//
// The sentence always carries the status when there is one, because "it didn't
// work" cannot be diagnosed and "blocked before it reached Aidey (403)" can.
// ─────────────────────────────────────────────────────────────────────────────

// `what` names the thing that did not happen, in the person's words:
// describeWriteFailure(err, 'save that note') → "Could not save that note…"
export function describeWriteFailure(err, what = 'save that') {
  const status = err?.status ?? null;
  const serverSaid = typeof err?.body?.error === 'string' ? err.body.error : null;

  if (status === 403) {
    return `Could not ${what}: the request was blocked before it reached Aidey (403). If it included an image or a lot of text, that is usually why.`;
  }
  if (status === 401) {
    return `Could not ${what}: you are signed out. Sign in again and nothing will be lost.`;
  }
  if (status === 402) {
    return `Could not ${what}: your plan does not cover it.`;
  }
  if (status === 404) {
    return `Could not ${what}: it no longer exists. Refresh to see the current list.`;
  }
  if (status === 409) {
    return serverSaid || `Could not ${what}: it changed somewhere else first. Refresh and try again.`;
  }
  if (status === 413) {
    return `Could not ${what}: it is too large to store.`;
  }
  if (status >= 500) {
    return `Could not ${what} (${status}). ${serverSaid || 'Aidey had a problem, not you, try again in a moment.'}`;
  }
  if (status) {
    return `Could not ${what} (${status}). ${serverSaid || 'Nothing was changed.'}`;
  }
  return `Could not ${what}: Aidey could not be reached. Nothing was changed, check your connection and try again.`;
}

// For the common case of one piece of state holding the latest failure. Returns
// the sentence so a caller can also log it.
export function noteWriteFailure(setter, err, what) {
  const message = describeWriteFailure(err, what);
  if (typeof setter === 'function') setter(message);
  return message;
}
